/**
 * Hallyu4U Integrated System
 * Complete integration between Shopify theme and backend
 * Version: 1.0.0
 */

class Hallyu4UIntegratedSystem {
  constructor() {
    this.config = {
      apiBaseUrl: window.HALLYU4U_CONFIG?.apiUrl || 'https://junie-unprotesting-jaelynn.ngrok-free.dev',
      websocketUrl: window.HALLYU4U_CONFIG?.wsUrl || 'wss://junie-unprotesting-jaelynn.ngrok-free.dev',
      shopifyDomain: window.Shopify?.shop || 'hallyu4u.myshopify.com',
      language: 'en', // Default language
      customer: null,
      authToken: null
    };

    this.modules = {
      auth: null,
      dashboard: null,
      packages: null,
      tracking: null,
      shipping: null,
      notifications: null
    };

    this.socket = null;
    this.initialized = false;
  }

  /**
   * Initialize the entire system
   */
  async init() {
    try {
      console.log('?? Initializing Hallyu4U Integrated System...');

      // Check if running in Shopify environment
      this.detectEnvironment();

      // Initialize authentication
      await this.initAuth();

      // Load customer data
      await this.loadCustomerData();

      // Initialize modules
      await this.initModules();

      // Setup WebSocket connection
      await this.setupWebSocket();

      // Setup global event listeners
      this.setupEventListeners();

      this.initialized = true;
      console.log('??Hallyu4U System initialized successfully');

      // Trigger ready event
      this.dispatchEvent('system:ready', { customer: this.config.customer });

      return true;
    } catch (error) {
      console.error('??Failed to initialize Hallyu4U System:', error);
      this.handleInitError(error);
      return false;
    }
  }

  /**
   * Detect and configure environment
   */
  detectEnvironment() {
    // Check if we're in Shopify
    if (window.Shopify) {
      this.config.environment = 'shopify';
      this.config.shopifyDomain = window.Shopify.shop;

      // Get customer from Shopify
      if (window.__st && window.__st.cid) {
        this.config.customerId = window.__st.cid;
      }
    } else {
      this.config.environment = 'standalone';
    }

    // Check for API URL in meta tags or script attributes
    const apiMeta = document.querySelector('meta[name="hallyu4u-api-url"]');
    if (apiMeta) {
      this.config.apiBaseUrl = apiMeta.content;
    }

    // Get language preference
    this.config.language = this.getLanguagePreference();
  }

  /**
   * Initialize authentication system
   */
  async initAuth() {
    this.modules.auth = new Hallyu4UAuth(this.config);

    // Try to restore session
    const existingToken = this.getStoredToken();
    if (existingToken) {
      try {
        const isValid = await this.modules.auth.validateToken(existingToken);
        if (isValid) {
          this.config.authToken = existingToken;
          console.log('??Restored existing session');
        } else {
          this.clearStoredToken();
          console.log('?�️ Previous session expired');
        }
      } catch (error) {
        console.error('Error validating token:', error);
      }
    }

    // Check if customer is logged in via Shopify
    if (this.config.environment === 'shopify' && this.config.customerId) {
      if (!this.config.authToken) {
        // Generate token from Shopify customer
        await this.authenticateShopifyCustomer();
      }
    }
  }

  /**
   * Authenticate Shopify customer with backend
   */
  async authenticateShopifyCustomer() {
    try {
      const response = await this.apiCall('/auth/shopify', {
        method: 'POST',
        body: JSON.stringify({
          customerId: this.config.customerId,
          shop: this.config.shopifyDomain
        })
      });

      if (response.success) {
        this.config.authToken = response.token;
        this.storeToken(response.token);
        console.log('??Shopify customer authenticated');
      }
    } catch (error) {
      console.error('Failed to authenticate Shopify customer:', error);
    }
  }

  /**
   * Load customer data from backend
   */
  async loadCustomerData() {
    if (!this.config.authToken) {
      console.log('?�️ No auth token, skipping customer data load');
      return;
    }

    try {
      const response = await this.apiCall('/customers/me');
      if (response.success) {
        this.config.customer = response.data;

        // Load unique code
        await this.loadUniqueCode();

        console.log('??Customer data loaded:', this.config.customer.email);
      }
    } catch (error) {
      console.error('Failed to load customer data:', error);
    }
  }

  /**
   * Load customer unique code
   */
  async loadUniqueCode() {
    try {
      const response = await this.apiCall('/unique-code/my-code');
      if (response.success) {
        this.config.customer.uniqueCode = response.data.code;
        this.config.customer.qrCode = response.data.qrCode;
        this.config.customer.barcode = response.data.barcode;

        // Update UI if dashboard exists
        this.updateUniqueCodeDisplay();
      }
    } catch (error) {
      console.error('Failed to load unique code:', error);
    }
  }

  /**
   * Initialize all system modules
   */
  async initModules() {
    // Dashboard module
    if (document.querySelector('.h4u-dashboard, .h4u-dashboard-enhanced')) {
      this.modules.dashboard = new Hallyu4UDashboard(this.config, this);
      await this.modules.dashboard.init();
    }

    // Package management module
    this.modules.packages = new Hallyu4UPackages(this.config, this);

    // Tracking module
    this.modules.tracking = new Hallyu4UTracking(this.config, this);

    // Shipping calculator module
    if (document.querySelector('.shipping-calculator')) {
      this.modules.shipping = new Hallyu4UShipping(this.config, this);
      await this.modules.shipping.init();
    }

    // Notification module
    this.modules.notifications = new Hallyu4UNotifications(this.config, this);

    console.log('??All modules initialized');
  }

  /**
   * Setup WebSocket connection for real-time updates
   */
  async setupWebSocket() {
    if (!this.config.authToken) {
      console.log('?�️ No auth token, skipping WebSocket setup');
      return;
    }

    try {
      // Use Socket.io if available
      if (typeof io !== 'undefined') {
        this.socket = io(this.config.websocketUrl, {
          auth: {
            token: this.config.authToken
          },
          transports: ['websocket', 'polling']
        });

        this.socket.on('connect', () => {
          console.log('??WebSocket connected');

          // Subscribe to customer updates
          if (this.config.customer) {
            this.socket.emit('subscribe', {
              customerId: this.config.customer._id || this.config.customer.id,
              channels: ['packages', 'notifications', 'tracking']
            });
          }
        });

        this.socket.on('disconnect', () => {
          console.log('?�️ WebSocket disconnected');
        });

        // Handle real-time events
        this.setupSocketEventHandlers();
      }
    } catch (error) {
      console.error('Failed to setup WebSocket:', error);
    }
  }

  /**
   * Setup WebSocket event handlers
   */
  setupSocketEventHandlers() {
    if (!this.socket) return;

    // Package updates
    this.socket.on('package:update', (data) => {
      console.log('?�� Package update:', data);
      this.modules.packages?.handleUpdate(data);
      this.modules.dashboard?.updatePackage(data);
    });

    // New package arrival
    this.socket.on('package:new', (data) => {
      console.log('?�� New package arrived:', data);
      this.modules.notifications?.show(`New package arrived: ${data.trackingNumber}`, 'success');
      this.modules.dashboard?.addPackage(data);
    });

    // Tracking updates
    this.socket.on('tracking:update', (data) => {
      console.log('?�� Tracking update:', data);
      this.modules.tracking?.handleUpdate(data);
    });

    // System notifications
    this.socket.on('notification', (data) => {
      console.log('?�� Notification:', data);
      this.modules.notifications?.show(data.message, data.type);
    });

    // Points update
    this.socket.on('points:update', (data) => {
      console.log('?�� Points update:', data);
      if (this.config.customer) {
        this.config.customer.points = data.points;
        this.config.customer.tier = data.tier;
        this.modules.dashboard?.updatePointsDisplay();
      }
    });
  }

  /**
   * Setup global event listeners
   */
  setupEventListeners() {
    // Handle authentication events
    document.addEventListener('hallyu4u:login', (e) => {
      this.handleLogin(e.detail);
    });

    document.addEventListener('hallyu4u:logout', () => {
      this.handleLogout();
    });

    // Handle language changes
    document.addEventListener('hallyu4u:language-change', (e) => {
      this.changeLanguage(e.detail.language);
    });

    // Handle page navigation (for SPA-like behavior)
    if (window.history && window.history.pushState) {
      window.addEventListener('popstate', (e) => {
        this.handleNavigation(e);
      });
    }
  }

  /**
   * Make API call with authentication
   */
  async apiCall(endpoint, options = {}) {
    const url = `${this.config.apiBaseUrl}/api${endpoint}`;

    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'ngrok-skip-browser-warning': 'true',
      ...options.headers
    };

    // Add auth token if available
    if (this.config.authToken) {
      headers['Authorization'] = `Bearer ${this.config.authToken}`;
    }

    // Add Shopify headers if in Shopify environment
    if (this.config.environment === 'shopify') {
      headers['X-Shopify-Domain'] = this.config.shopifyDomain;
      headers['X-Shopify-Customer-Id'] = this.config.customerId;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Handle unauthorized
          this.handleUnauthorized();
        }
        throw new Error(`API call failed: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API call error:', error);
      throw error;
    }
  }

  /**
   * Handle login
   */
  async handleLogin(credentials) {
    try {
      const response = await this.apiCall('/auth/login', {
        method: 'POST',
        body: JSON.stringify(credentials)
      });

      if (response.success) {
        this.config.authToken = response.token;
        this.config.customer = response.customer;
        this.storeToken(response.token);

        // Reinitialize system with authenticated user
        await this.init();

        this.dispatchEvent('system:authenticated', { customer: this.config.customer });
      }

      return response;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }

  /**
   * Handle logout
   */
  async handleLogout() {
    try {
      await this.apiCall('/auth/logout', { method: 'POST' });
    } catch (error) {
      console.error('Logout error:', error);
    }

    // Clear local state
    this.config.authToken = null;
    this.config.customer = null;
    this.clearStoredToken();

    // Disconnect WebSocket
    if (this.socket) {
      this.socket.disconnect();
    }

    // Reload or redirect
    if (this.config.environment === 'shopify') {
      window.location.href = '/account/logout';
    } else {
      window.location.reload();
    }
  }

  /**
   * Handle unauthorized response
   */
  handleUnauthorized() {
    console.log('?�️ Unauthorized, clearing session');
    this.clearStoredToken();
    this.config.authToken = null;

    // Redirect to login
    if (this.config.environment === 'shopify') {
      window.location.href = '/account/login';
    } else {
      this.dispatchEvent('system:unauthorized');
    }
  }

  /**
   * Update unique code display
   */
  updateUniqueCodeDisplay() {
    const codeElement = document.getElementById('customer-unique-code');
    if (codeElement && this.config.customer?.uniqueCode) {
      codeElement.textContent = this.config.customer.uniqueCode;

      // Update Korean address code
      const addressCode = document.getElementById('korean-address-code');
      if (addressCode) {
        addressCode.textContent = this.config.customer.uniqueCode;
      }

      // Display QR code if element exists
      const qrContainer = document.getElementById('unique-code-qr');
      if (qrContainer && this.config.customer.qrCode) {
        qrContainer.innerHTML = `<img src="${this.config.customer.qrCode}" alt="QR Code" class="w-32 h-32">`;
      }
    }
  }

  /**
   * Change system language
   */
  async changeLanguage(language) {
    this.config.language = language;
    localStorage.setItem('h4u_language', language);

    // Update all modules
    Object.values(this.modules).forEach(module => {
      if (module && typeof module.changeLanguage === 'function') {
        module.changeLanguage(language);
      }
    });

    // Reload translations
    await this.loadTranslations(language);

    this.dispatchEvent('system:language-changed', { language });
  }

  /**
   * Load translations for the specified language
   */
  async loadTranslations(language) {
    try {
      const response = await fetch(`/locales/${language}.json`);
      const translations = await response.json();
      window.H4U_TRANSLATIONS = translations;
      return translations;
    } catch (error) {
      console.error(`Failed to load translations for ${language}:`, error);
      return {};
    }
  }

  /**
   * Get language preference
   */
  getLanguagePreference() {
    // Check localStorage
    const stored = localStorage.getItem('h4u_language');
    if (stored) return stored;

    // Check browser language
    const browserLang = navigator.language.substring(0, 2);
    const supportedLangs = ['en', 'ko', 'ja', 'zh', 'es'];

    if (supportedLangs.includes(browserLang)) {
      return browserLang;
    }

    return 'en'; // Default to English
  }

  /**
   * Translate key
   */
  t(key, replacements = {}) {
    const translations = window.H4U_TRANSLATIONS || {};
    let text = translations[key] || key;

    // Replace placeholders
    Object.keys(replacements).forEach(placeholder => {
      text = text.replace(`{{${placeholder}}}`, replacements[placeholder]);
    });

    return text;
  }

  /**
   * Token management
   */
  getStoredToken() {
    return localStorage.getItem('h4u_token') || sessionStorage.getItem('h4u_token');
  }

  storeToken(token, remember = true) {
    if (remember) {
      localStorage.setItem('h4u_token', token);
    } else {
      sessionStorage.setItem('h4u_token', token);
    }
  }

  clearStoredToken() {
    localStorage.removeItem('h4u_token');
    sessionStorage.removeItem('h4u_token');
  }

  /**
   * Dispatch custom event
   */
  dispatchEvent(eventName, detail = {}) {
    const event = new CustomEvent(`hallyu4u:${eventName}`, {
      detail,
      bubbles: true,
      cancelable: true
    });
    document.dispatchEvent(event);
  }

  /**
   * Handle initialization error
   */
  handleInitError(error) {
    // Show user-friendly error message
    const errorContainer = document.getElementById('h4u-error-container');
    if (errorContainer) {
      errorContainer.innerHTML = `
        <div class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <strong class="font-bold">System Error</strong>
          <span class="block sm:inline">Unable to initialize Hallyu4U system. Please refresh the page or contact support.</span>
        </div>
      `;
    }

    // Log detailed error for debugging
    console.error('Initialization error details:', {
      error,
      config: this.config,
      environment: this.config.environment
    });
  }

  /**
   * Handle navigation (SPA-like)
   */
  handleNavigation(event) {
    const path = window.location.pathname;
    console.log('Navigating to:', path);

    // Update active state in dashboard
    if (this.modules.dashboard) {
      this.modules.dashboard.handleNavigation(path);
    }
  }

  /**
   * Public API methods
   */

  // Get current customer
  getCustomer() {
    return this.config.customer;
  }

  // Get auth status
  isAuthenticated() {
    return !!this.config.authToken;
  }

  // Get current language
  getLanguage() {
    return this.config.language;
  }

  // Get system config
  getConfig() {
    return { ...this.config };
  }

  // Check if system is ready
  isReady() {
    return this.initialized;
  }
}

/**
 * Dashboard Module
 */
class Hallyu4UDashboard {
  constructor(config, system) {
    this.config = config;
    this.system = system;
    this.packages = [];
    this.selectedPackages = new Set();
    this.currentTab = 'warehouse';
  }

  async init() {
    console.log('Initializing dashboard module...');

    // Load dashboard data
    await this.loadDashboardData();

    // Setup dashboard UI
    this.setupUI();

    // Setup event listeners
    this.setupEventListeners();
  }

  async loadDashboardData() {
    try {
      // Load statistics
      const stats = await this.system.apiCall(`/customers/${this.config.customer._id}/statistics`);
      this.updateStatistics(stats.data);

      // Load packages
      const packages = await this.system.apiCall(`/packages?customerId=${this.config.customer._id}`);
      this.packages = packages.data || [];
      this.renderPackages();

    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    }
  }

  setupUI() {
    // Initialize tooltips, modals, etc.
    this.initializeTooltips();
    this.initializeModals();
  }

  setupEventListeners() {
    // Tab switching
    document.querySelectorAll('.tab-btn').forEach(btn => {
      btn.addEventListener('click', (e) => this.switchTab(e.target.dataset.tab));
    });

    // Package selection
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('package-checkbox')) {
        this.togglePackageSelection(e.target.dataset.packageId, e.target.checked);
      }
    });

    // Action buttons
    document.querySelector('.consolidate-btn')?.addEventListener('click', () => this.requestConsolidation());
    document.querySelector('.ship-request-btn')?.addEventListener('click', () => this.requestShipping());
  }

  updateStatistics(stats) {
    // Update UI elements
    document.getElementById('total-packages').textContent = stats.totalPackages || 0;
    document.getElementById('in-warehouse').textContent = stats.inWarehouse || 0;
    document.getElementById('pending-ship').textContent = stats.pendingShip || 0;
    document.getElementById('shipped').textContent = stats.shipped || 0;
    document.getElementById('total-saved').textContent = `$${(stats.totalSaved || 0).toLocaleString()}`;
  }

  renderPackages() {
    const container = document.getElementById('warehouse-packages');
    if (!container) return;

    container.innerHTML = '';

    const warehousePackages = this.packages.filter(pkg => pkg.status === 'storage');

    if (warehousePackages.length === 0) {
      container.innerHTML = this.getEmptyStateHTML();
      return;
    }

    warehousePackages.forEach(pkg => {
      container.appendChild(this.createPackageCard(pkg));
    });
  }

  createPackageCard(pkg) {
    const card = document.createElement('div');
    card.className = 'package-card bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition';
    card.dataset.packageId = pkg._id;

    card.innerHTML = `
      <div class="relative">
        ${pkg.photoUrl ? `
          <img src="${pkg.photoUrl}" alt="Package ${pkg.trackingNumber}" class="w-full h-48 object-cover">
        ` : this.getPlaceholderImageHTML()}
        <div class="absolute top-2 right-2">
          <span class="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-600">
            In Warehouse
          </span>
        </div>
        <div class="absolute top-2 left-2">
          <input type="checkbox" class="package-checkbox" data-package-id="${pkg._id}">
        </div>
      </div>
      <div class="p-4">
        <h4 class="font-semibold text-gray-800 mb-2">${pkg.trackingNumber}</h4>
        <div class="space-y-1 text-sm text-gray-600">
          <p>Weight: ${pkg.weight}kg</p>
          <p>Arrived: ${new Date(pkg.arrivalDate).toLocaleDateString()}</p>
          <p>Days remaining: ${this.calculateDaysRemaining(pkg.arrivalDate)}</p>
        </div>
      </div>
    `;

    return card;
  }

  getEmptyStateHTML() {
    return `
      <div class="col-span-full text-center py-12">
        <svg class="w-20 h-20 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
        </svg>
        <h3 class="text-lg font-medium text-gray-900 mb-2">No packages in warehouse</h3>
        <p class="text-gray-500">Your packages will appear here when they arrive at our warehouse.</p>
      </div>
    `;
  }

  getPlaceholderImageHTML() {
    return `
      <div class="w-full h-48 bg-gray-100 flex items-center justify-center">
        <svg class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
        </svg>
      </div>
    `;
  }

  switchTab(tabName) {
    this.currentTab = tabName;
    // Update UI
    document.querySelectorAll('.tab-btn').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.tab === tabName);
    });

    document.querySelectorAll('.tab-content').forEach(content => {
      content.classList.toggle('hidden', !content.id.startsWith(tabName));
    });
  }

  togglePackageSelection(packageId, selected) {
    if (selected) {
      this.selectedPackages.add(packageId);
    } else {
      this.selectedPackages.delete(packageId);
    }

    // Update action buttons
    const hasSelection = this.selectedPackages.size > 0;
    document.querySelector('.consolidate-btn').disabled = !hasSelection;
    document.querySelector('.ship-request-btn').disabled = !hasSelection;
  }

  calculateDaysRemaining(arrivalDate) {
    const arrival = new Date(arrivalDate);
    const expiry = new Date(arrival);
    expiry.setDate(expiry.getDate() + 45);
    const today = new Date();
    const days = Math.floor((expiry - today) / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  }

  async requestConsolidation() {
    // Implementation for consolidation request
    console.log('Requesting consolidation for:', Array.from(this.selectedPackages));
  }

  async requestShipping() {
    // Implementation for shipping request
    console.log('Requesting shipping for:', Array.from(this.selectedPackages));
  }

  updatePackage(packageData) {
    // Update package in local state
    const index = this.packages.findIndex(p => p._id === packageData._id);
    if (index !== -1) {
      this.packages[index] = packageData;
      this.renderPackages();
    }
  }

  addPackage(packageData) {
    this.packages.push(packageData);
    this.renderPackages();
  }

  initializeTooltips() {
    // Initialize tooltips if library is available
  }

  initializeModals() {
    // Initialize modals if needed
  }

  handleNavigation(path) {
    // Handle navigation within dashboard
  }

  changeLanguage(language) {
    // Update UI text based on language
  }

  updatePointsDisplay() {
    // Update points display in UI
  }
}

/**
 * Packages Module
 */
class Hallyu4UPackages {
  constructor(config, system) {
    this.config = config;
    this.system = system;
  }

  handleUpdate(data) {
    console.log('Package update received:', data);
    // Handle package updates
  }
}

/**
 * Tracking Module
 */
class Hallyu4UTracking {
  constructor(config, system) {
    this.config = config;
    this.system = system;
  }

  handleUpdate(data) {
    console.log('Tracking update received:', data);
    // Handle tracking updates
  }
}

/**
 * Shipping Module
 */
class Hallyu4UShipping {
  constructor(config, system) {
    this.config = config;
    this.system = system;
  }

  async init() {
    console.log('Initializing shipping module...');
    // Initialize shipping calculator
  }
}

/**
 * Notifications Module
 */
class Hallyu4UNotifications {
  constructor(config, system) {
    this.config = config;
    this.system = system;
  }

  show(message, type = 'info') {
    const notification = document.createElement('div');
    notification.className = `fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg text-white z-50 transform transition-all duration-300 translate-y-full`;

    const bgColors = {
      'success': 'bg-green-500',
      'error': 'bg-red-500',
      'warning': 'bg-yellow-500',
      'info': 'bg-blue-500'
    };

    notification.classList.add(bgColors[type] || bgColors.info);
    notification.textContent = message;

    document.body.appendChild(notification);

    // Animate in
    setTimeout(() => {
      notification.classList.remove('translate-y-full');
    }, 10);

    // Remove after 3 seconds
    setTimeout(() => {
      notification.classList.add('translate-y-full');
      setTimeout(() => {
        notification.remove();
      }, 300);
    }, 3000);
  }
}

/**
 * Authentication Module
 */
class Hallyu4UAuth {
  constructor(config) {
    this.config = config;
  }

  async validateToken(token) {
    try {
      const response = await fetch(`${this.config.apiBaseUrl}/api/auth/validate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.ok;
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  }
}

/**
 * Initialize on DOM ready
 */
if (typeof window !== 'undefined') {
  // Create global instance
  window.Hallyu4U = new Hallyu4UIntegratedSystem();

  // Auto-initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.Hallyu4U.init();
    });
  } else {
    // DOM is already loaded
    window.Hallyu4U.init();
  }

  // Export for use in other scripts
  window.Hallyu4UIntegratedSystem = Hallyu4UIntegratedSystem;
}
